import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  FilterChipColor,
  FilterChipIconSet,
  FilterChipShape,
  FilterChipSize,
  MUNILY_FILTER_CHIP_DEFAULTS,
} from './filter-chip.types';

/**
 * `<app-filter-chip>` — toggleable chip for filter UIs / segmented options.
 *
 * Anatomy (Material 3 filter chip):
 *   - Unselected: outlined chip, gray border, neutral text.
 *   - Selected:   filled chip, `check_circle` icon at the leading slot.
 *   - Disabled:   reduced opacity, no pointer-events.
 *
 * Color tones come from the canonical `_color-tokens.scss` map — variants are
 * driven by class-name flags computed in TS (R21 — clases dinámicas).
 *
 * API:
 *   - `[label]`       — visible text
 *   - `[selected]`    — current toggle state (one-way)
 *   - `[disabled]`    — disabled
 *   - `[size]`        — `'small' | 'large'`
 *   - `[shape]`       — `'rounded-small' | 'rounded-large'`
 *   - `[color]`       — `'gray' | 'blue'`
 *   - `[iconLeading]` — overrides the default `check_circle` when selected
 *   - `[iconSet]`     — material symbols set
 *   - `(selectedChange)` — emits the new boolean state on each toggle.
 */
@Component({
  selector: 'app-filter-chip',
  templateUrl: './filter-chip.component.html',
  styleUrls: ['./filter-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipComponent implements OnChanges {
  @Input() label = '';
  @Input() selected = false;
  @Input() disabled = false;
  @Input() size: FilterChipSize = MUNILY_FILTER_CHIP_DEFAULTS.size;
  @Input() shape: FilterChipShape = MUNILY_FILTER_CHIP_DEFAULTS.shape;
  @Input() color: FilterChipColor = MUNILY_FILTER_CHIP_DEFAULTS.color;
  @Input() iconLeading?: string;
  @Input() iconSet: FilterChipIconSet = MUNILY_FILTER_CHIP_DEFAULTS.iconSet;

  @Output() selectedChange = new EventEmitter<boolean>();

  /** Pre-computed class list (R21 — avoid multiple `[class.x]` bindings). */
  rootClasses = '';

  /** Resolved icon to render (selected → `check_circle` default). */
  resolvedIcon: string | undefined;

  ngOnChanges(_: SimpleChanges): void {
    this.recomputeView();
  }

  toggle(): void {
    if (this.disabled) return;
    this.selectedChange.emit(!this.selected);
  }

  private recomputeView(): void {
    this.resolvedIcon = this.iconLeading
      ? this.iconLeading
      : this.selected
        ? MUNILY_FILTER_CHIP_DEFAULTS.selectedIcon
        : undefined;

    this.rootClasses = [
      'munily-filter-chip',
      `munily-filter-chip--color-${this.color}`,
      `munily-filter-chip--size-${this.size}`,
      `munily-filter-chip--shape-${this.shape}`,
      this.selected ? 'is-selected' : 'is-unselected',
      this.disabled ? 'is-disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }
}
